/**
 * Centralized External Link Configuration
 *
 * All external URLs, phone numbers, and social platforms in one place.
 * This ensures consistency across the app and makes updates easy.
 */

// =============================================================================
// SUPPORT CONTACTS
// =============================================================================

export const SUPPORT_CONTACTS = {
  /** Betty's WhatsApp number (Ghana format, no +) */
  BETTY_WHATSAPP: '233555496446',
  /** Default message when contacting Betty */
  BETTY_DEFAULT_MESSAGE: 'Hi Betty! I have a question about Motito.',
  /** Support email */
  SUPPORT_EMAIL: 'hello@motito.co',
  /** Support phone */
  SUPPORT_PHONE: '+233 59 601 1668',
} as const;

// =============================================================================
// EXTERNAL URLS
// =============================================================================

export const EXTERNAL_URLS = {
  // Help & Support
  HELP_CENTER: 'https://support.motito.co',
  HELP_CENTER_ARTICLES: {
    REP_SCORE:
      'https://support.motito.co/hc/pay-small-small-help-center/articles/1766101162-what-is-my_rep_score-and-why-it-matters',
    EARLY_PICKUP:
      'https://support.motito.co/hc/pay-small-small-help-center/articles/1766101313-getting-your-item-before-you-finish-paying-early-pickup-pss-credit',
    PRICE_PROTECTION:
      'https://support.motito.co/hc/pay-small-small-help-center/articles/1766078822-price-protection-explained',
  },

  // Marketing Pages
  MARKETING: {
    ABOUT: 'https://motito.co/about-us.html',
    BUY_NOW_PAY_LATER: 'https://motito.co/buy-now-pay-later.html',
    PAY4ME: 'https://motito.co/pay4me.html',
    KEEP_MY_PRICE: 'https://motito.co/keep-my-price.html',
    BLOG: 'https://blog.motito.co/',
  },

  // Legal
  LEGAL: {
    TERMS: 'https://motito.co/terms-and-conditions.html',
    PRIVACY: 'https://motito.co/privacy-policy.html',
  },
} as const;

// =============================================================================
// SOCIAL PLATFORMS
// =============================================================================

export const SOCIAL_PLATFORMS = {
  WHATSAPP: {
    name: 'WhatsApp',
    /** Generate WhatsApp chat URL */
    chatUrl: (phone: string, message?: string) => {
      const encodedMessage = message ? encodeURIComponent(message) : '';
      return `https://wa.me/${phone}${encodedMessage ? `?text=${encodedMessage}` : ''}`;
    },
    /** Generate WhatsApp share URL (no specific recipient) */
    shareUrl: (message: string) =>
      `https://wa.me/?text=${encodeURIComponent(message)}`,
  },
  FACEBOOK: {
    name: 'Facebook',
    shareUrl: (url: string) =>
      `https://facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  TWITTER: {
    name: 'Twitter',
    shareUrl: (url: string, text?: string) => {
      const params = new URLSearchParams({ url });
      if (text) params.append('text', text);
      return `https://twitter.com/intent/tweet?${params}`;
    },
  },
  TELEGRAM: {
    name: 'Telegram',
    shareUrl: (url: string, text?: string) => {
      const params = new URLSearchParams({ url });
      if (text) params.append('text', text);
      return `https://t.me/share/url?${params}`;
    },
  },
  SMS: {
    name: 'SMS',
    shareUrl: (message: string) => `sms:?body=${encodeURIComponent(message)}`,
  },
} as const;

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type SupportContacts = typeof SUPPORT_CONTACTS;
export type ExternalUrls = typeof EXTERNAL_URLS;
export type SocialPlatforms = typeof SOCIAL_PLATFORMS;
